import type { FieldError } from '@propos/shared';

/**
 * The API client seam. Every server call goes through here so error shape,
 * credentials, and cache invalidation stay uniform (docs/08 conventions:
 * JSON only, errors {code, message}).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** The parsed error body — some errors carry payloads (409 duplicates). */
    public readonly body: unknown = null,
    /**
     * Which fields failed, when the server could say (docs/08's `fieldErrors`).
     * Empty for every other error, so a caller can read it without branching on
     * the code first.
     */
    public readonly fieldErrors: readonly FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A 400's field errors, defensively — a malformed body must not throw here. */
function readFieldErrors(body: unknown): readonly FieldError[] {
  if (typeof body !== 'object' || body === null) return [];
  const raw = (body as { fieldErrors?: unknown }).fieldErrors;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { path, message } = entry as { path?: unknown; message?: unknown };
    if (typeof path !== 'string' || typeof message !== 'string') return [];
    return [{ path, message }];
  });
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Headers are MERGED, not replaced, and merged through `Headers` because
  // HeadersInit may be a Record, an array of pairs, or a Headers instance —
  // spreading it would silently produce indices for two of the three. Spreading
  // `init` last would also drop `accept` for any caller that sets a content
  // type, which the photo upload does: it sends raw image bytes, not JSON
  // (ADR-25).
  const headers = new Headers({ accept: 'application/json' });
  if (init?.body !== undefined) headers.set('content-type', 'application/json');
  for (const [key, value] of new Headers(init?.headers).entries()) {
    headers.set(key, value);
  }
  const response = await fetch(path, { credentials: 'same-origin', ...init, headers });
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = body as { code?: string; message?: string } | null;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Request failed',
      body,
      readFieldErrors(body),
    );
  }
  return body as T;
}

/** /healthz — the unauthenticated probe, also the build-stamp source (docs/07). */
export interface Health {
  readonly status: string;
  readonly env: string;
  readonly version: string;
}

export interface Me {
  readonly userId: string;
  readonly status: 'pending' | 'active';
  readonly roleKey: string | null;
  readonly email: string;
  readonly displayName: string;
  /** Materialized permission keys, declared by the server (docs/03 §4). */
  readonly permissions: readonly string[];
  /**
   * Present only while this session is borrowing someone's eyes (docs/01 §6).
   * Everything above then describes the PERSON BEING VIEWED — which is what
   * makes View As worth having, and exactly why the banner is not optional.
   */
  readonly impersonation: {
    readonly viewingName: string;
    readonly adminDisplayName: string;
    readonly mode: 'view' | 'act';
    readonly startedAt: string;
  } | null;
}
